import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** The kinds a message can be. What each is called is in the catalogue. */
const MESSAGE_TYPES = ["bug", "feature", "other"] as const;

type MessageType = (typeof MESSAGE_TYPES)[number];

const contactSchema = z.object({
  messageType: z.enum(["bug", "feature", "other"]),
  email: z
    .string()
    .trim()
    .max(255, { message: "Email must be less than 255 characters" })
    .email({ message: "Invalid email address" })
    .optional()
    .or(z.literal("")),
  message: z
    .string()
    .trim()
    .nonempty({ message: "Message cannot be empty" })
    .max(1000, { message: "Message must be less than 1000 characters" }),
});

interface ContactUsFormProps {
  roomId?: string;
  /** Called once a message reaches the backend. */
  onSent?: () => void;
}

export function ContactUsForm({ roomId, onSent }: ContactUsFormProps) {
  const { t } = useTranslation();
  const [messageType, setMessageType] = useState<MessageType>("feature");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = contactSchema.safeParse({ messageType, email, message });
    if (!parsed.success) return;

    setIsSubmitting(true);
    const { error } = await supabase.from("contact_messages").insert({
      message_type: parsed.data.messageType,
      email: parsed.data.email ? parsed.data.email : null,
      message: parsed.data.message,
      room_id: roomId ?? null,
    });
    setIsSubmitting(false);

    if (error) return;

    setMessage("");
    setEmail("");
    setIsSent(true);
    onSent?.();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3"
      data-testid="contact-form"
    >
      <p className="text-xs text-muted-foreground">{t("contact.intro")}</p>

      <div className="space-y-1.5">
        <Label htmlFor="contact-type">{t("contact.type")}</Label>
        <Select
          value={messageType}
          onValueChange={(value) => {
            const next = MESSAGE_TYPES.find((kind) => kind === value);
            if (next) setMessageType(next);
          }}
        >
          <SelectTrigger id="contact-type" className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MESSAGE_TYPES.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {t(`contact.type${kind[0].toUpperCase()}${kind.slice(1)}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-email">{t("contact.emailOptional")}</Label>
        <Input
          id="contact-email"
          type="email"
          value={email}
          maxLength={255}
          placeholder={t("contact.emailPlaceholder")}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-message">{t("contact.message")}</Label>
        <Textarea
          id="contact-message"
          value={message}
          maxLength={1000}
          rows={4}
          placeholder={t("contact.messagePlaceholder")}
          onChange={(e) => {
            setMessage(e.target.value);
            setIsSent(false);
          }}
        />
        <p className="text-xs text-muted-foreground text-right">
          {message.length}/1000
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        {isSent ? (
          <span className="text-xs text-muted-foreground">
            {t("contact.sent")}
          </span>
        ) : (
          <span />
        )}
        <Button
          type="submit"
          size="sm"
          disabled={isSubmitting}
          className="gap-1.5"
        >
          <Send className="w-4 h-4" />
          {isSubmitting ? t("contact.sending") : t("contact.send")}
        </Button>
      </div>
    </form>
  );
}
